import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import { Claim, EdiObject, FeeChange } from "../../../../../types/claims";
import { apiService } from "../../../../../services/apiService";
import useNotifier from "../../../../../hooks/useNotifier";
import { EditErrorAlert } from "../record-payment/ClaimEditDialogParts";
import JoinPaymentConfirm from "./JoinPaymentConfirm";
import RemitPicker from "./RemitPicker";
import {
  JoinPaymentError,
  findByAttachmentId,
  getJoinCandidates,
  parseJoinPaymentError,
} from "./joinPayments";

interface JoinPaymentDialogProps {
  open: boolean;
  claim: Claim;
  /** The manual payment to join. */
  manual: EdiObject;
  /** The claim's EDI objects, where the remits come from. */
  ediObjects: EdiObject[];
  /** The remit a duplicate warning names: skips choosing and goes straight to the confirmation. */
  remitAttachmentId?: string;
  onClose: () => void;
  /** Called once they're joined, or when the API says the manual payment is
   * already gone (removed or joined), so the caller refreshes the claim. */
  onJoined: () => void;
}

type Step = "choose" | "confirm";

const isGone = (error: JoinPaymentError) =>
  error.code === "not_found" || error.code === "payment_not_active";

// Refusals that are about the chosen remit: another one may still be joined
const isAboutRemit = (error: JoinPaymentError) =>
  !!error.fieldErrors.remitAttachmentId ||
  error.code === "remit_already_joined";

/**
 * Admin-only: joins a manual payment with the electronic remit (835) that
 * duplicates it, so the payment counts once. The admin picks the remit (likely
 * matches first) unless a duplicate warning already named it. Review asks the
 * API for a dry run (every check, nothing saved) and shows its effect on the
 * claim; only confirming joins them. There is no unjoin.
 */
const JoinPaymentDialog: FC<JoinPaymentDialogProps> = ({
  open,
  claim,
  manual,
  ediObjects,
  remitAttachmentId,
  onClose,
  onJoined,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();
  const canChoose = !remitAttachmentId;

  // Set at mount too: the open effect runs after the first paint
  const [step, setStep] = useState<Step>(
    remitAttachmentId ? "confirm" : "choose",
  );
  const [remitId, setRemitId] = useState<string | null>(
    remitAttachmentId ?? null,
  );
  const [fee, setFee] = useState<FeeChange | null>(null);
  const [isReviewed, setIsReviewed] = useState(false);
  const [chooseError, setChooseError] = useState<string | null>(null);
  const [error, setError] = useState<JoinPaymentError | null>(null);
  const [isChecking, setIsChecking] = useState(!!remitAttachmentId);
  const [isJoining, setIsJoining] = useState(false);

  const candidates = getJoinCandidates(
    manual,
    ediObjects,
    claim.dataWarnings?.possible_duplicate_payments,
  );
  const remit = remitId ? findByAttachmentId(ediObjects, remitId) : undefined;
  const attachmentId = manual.attachmentId as string;

  const join = (id: string, dryRun: boolean) =>
    apiService.joinClaimManualPayment(claim.id, attachmentId, id, dryRun);

  /** A refusal: a payment that's already gone refreshes the claim, a remit that
   * can't take it goes back to choosing, anything else shows on the summary. */
  const handleRefusal = (refusal: JoinPaymentError) => {
    if (isGone(refusal)) {
      notifier.showError(refusal.message);
      onJoined();
      onClose();
      return;
    }
    if (canChoose && isAboutRemit(refusal)) {
      setChooseError(refusal.fieldErrors.remitAttachmentId || refusal.message);
      setStep("choose");
      return;
    }
    setError(refusal);
    setStep("confirm");
  };

  const review = async (id: string) => {
    setIsChecking(true);
    setError(null);
    setChooseError(null);
    setIsReviewed(false);
    try {
      const response = await join(id, true);
      if (response.success && response.data) {
        setFee(response.data.fee);
        setIsReviewed(true);
        setStep("confirm");
        return;
      }
      handleRefusal(parseJoinPaymentError(response));
    } finally {
      setIsChecking(false);
    }
  };

  const handleJoin = async () => {
    if (!remitId) return;
    setIsJoining(true);
    setError(null);
    try {
      const response = await join(remitId, false);
      if (response.success) {
        notifier.showSuccess("Payment joined with the remit.");
        onJoined();
        onClose();
        return;
      }
      handleRefusal(parseJoinPaymentError(response));
    } finally {
      setIsJoining(false);
    }
  };

  // Reset on close as well as on open, so a reopened dialog never paints the
  // last session's step or error; a remit named by a warning is reviewed at once
  useEffect(() => {
    setRemitId(remitAttachmentId ?? null);
    setStep(remitAttachmentId ? "confirm" : "choose");
    setFee(null);
    setIsReviewed(false);
    setChooseError(null);
    setError(null);
    setIsJoining(false);
    setIsChecking(open && !!remitAttachmentId);
    if (open && remitAttachmentId) void review(remitAttachmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, remitAttachmentId]);

  const handleClose = () => {
    if (isJoining) return;
    onClose();
  };

  const isBlocked = !!error;

  const renderChoose = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
        Choose the 835 that duplicates this manual payment. Only remits from the
        same payer level are listed.
      </Typography>
      {chooseError && <EditErrorAlert message={chooseError} />}
      {candidates.length > 0 ? (
        <RemitPicker
          candidates={candidates}
          selectedId={remitId}
          disabled={isChecking}
          onSelect={(id) => {
            setRemitId(id);
            setChooseError(null);
          }}
        />
      ) : (
        <Alert severity="info" sx={{ maxWidth: "100%" }}>
          This claim has no electronic remit from the same payer level.
        </Alert>
      )}
    </Box>
  );

  const renderConfirm = () => {
    if (isChecking) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 4 }}
          aria-label="Checking the join"
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {remit ? (
          <JoinPaymentConfirm
            claim={claim}
            manual={manual}
            remit={remit}
            fee={fee}
            blocked={isBlocked}
          />
        ) : null}
        {error && (
          <EditErrorAlert
            message={error.message}
            onRetry={
              error.code === "unknown" && remitId
                ? () => void (isReviewed ? handleJoin() : review(remitId))
                : undefined
            }
            isRetrying={isChecking || isJoining}
          />
        )}
      </Box>
    );
  };

  const backButton = canChoose && (
    <Button
      onClick={() => {
        setError(null);
        setStep("choose");
      }}
      variant="outlined"
      disabled={isJoining || isChecking}
    >
      Back
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        {step === "choose" ? "Join with remit" : "Confirm join"}
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          Claim {claim.claimNumber}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ pt: 2 }}>
          {step === "choose" ? renderChoose() : renderConfirm()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {step === "choose" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isChecking}
            >
              Cancel
            </Button>
            <Button
              onClick={() => remitId && void review(remitId)}
              variant="contained"
              disabled={!remitId}
              loading={isChecking}
            >
              Review
            </Button>
          </>
        ) : isBlocked ? (
          <>
            {backButton}
            <Button onClick={handleClose} variant="outlined">
              Close
            </Button>
          </>
        ) : (
          <>
            {backButton || (
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={isJoining}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleJoin}
              variant="contained"
              disabled={isChecking || !isReviewed}
              loading={isJoining}
            >
              Confirm and join
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JoinPaymentDialog;
